import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import DoDisturbAltRoundedIcon from "@mui/icons-material/DoDisturbAltRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { Link as RouterLink } from "react-router-dom";
import { adminApi } from "../api/client";

interface QuotationListItem {
  id: number;
  quote_number: string;
  revision_no: number;
  status: string;
  title: string;
  issue_date?: string | null;
  valid_until?: string | null;
  currency_code: string;
  pricing_mode: string;
  grand_total: number;
  shared_at?: string | null;
  first_viewed_at?: string | null;
  granted_at?: string | null;
  customer_name?: string | null;
  event_title?: string | null;
  event_start_at?: string | null;
  category_names?: string | null;
  category_codes?: string | null;
  categoryNames?: string | null;
  sections?: Array<{
    sectionTitle?: string | null;
    section_title?: string | null;
    isVisible?: boolean | number | null;
    is_visible?: boolean | number | null;
  }>;
}

const currency = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0
});

function statusColor(status: string): "default" | "primary" | "success" | "warning" | "error" {
  switch (status) {
    case "granted":
      return "success";
    case "pending":
      return "warning";
    case "rejected":
      return "error";
    default:
      return "default";
  }
}

function getQuotationCategoryNames(row: QuotationListItem) {
  const directNames = row.category_names || row.categoryNames;

  if (directNames) {
    return directNames;
  }

  const sectionNames = (row.sections || [])
    .filter((section) => section.isVisible !== false && section.isVisible !== 0 && section.is_visible !== false && section.is_visible !== 0)
    .map((section) => String(section.sectionTitle || section.section_title || "").trim())
    .filter(Boolean);

  return Array.from(new Set(sectionNames)).join(", ");
}

function formatDisplayDate(value?: string | null) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
}

export default function QuotationsPage() {
  const [rows, setRows] = useState<QuotationListItem[]>([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteTarget, setDeleteTarget] = useState<QuotationListItem | null>(null);
  const [categoryPopover, setCategoryPopover] = useState<{ anchorEl: HTMLElement; text: string } | null>(null);

  const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const loadRows = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (search) params.set("search", search);
      const suffix = params.toString() ? `?${params.toString()}` : "";
      const response = await adminApi.get<QuotationListItem[]>(`/quotations${suffix}`);
      const rowsWithCategories = await Promise.all(
        response.map(async (row) => {
          if (getQuotationCategoryNames(row)) {
            return row;
          }

          try {
            const detail = await adminApi.get<QuotationListItem>(`/quotations/${row.id}?view=editor`);
            const categoryNames = getQuotationCategoryNames(detail);
            return categoryNames ? { ...row, category_names: categoryNames } : row;
          } catch (detailError) {
            return row;
          }
        })
      );
      setRows(rowsWithCategories);
      setPage(0);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load quotations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadRows();
  }, []);

  const updateStatus = async (id: number, nextStatus: string) => {
    try {
      await adminApi.patch(`/quotations/${id}/status`, {
        status: nextStatus,
        reason: `Status updated to ${nextStatus} from admin`
      });
      await loadRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update status");
    }
  };

  const duplicate = async (id: number) => {
    try {
      await adminApi.post(`/quotations/${id}/duplicate`, {});
      await loadRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to duplicate quotation");
    }
  };

  const deleteQuotation = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      setLoading(true);
      await adminApi.delete(`/quotations/${deleteTarget.id}`);
      setDeleteTarget(null);
      await loadRows();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete quotation");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, nextPage: number) => {
    setPage(nextPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const openCategoryPopover = (event: React.MouseEvent<HTMLElement>, text: string) => {
    if (!text) {
      return;
    }

    setCategoryPopover({
      anchorEl: event.currentTarget,
      text
    });
  };

  const closeCategoryPopover = () => {
    setCategoryPopover(null);
  };

  return (
    <Stack spacing={3}>
      <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.94)" }}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
        >
          <div style={{ minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Quotations
            </Typography>
            <Typography sx={{ color: "text.secondary" }}>
              Manage pending, granted, revised, and printable quotations from one workflow table.
            </Typography>
          </div>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: "stretch", lg: "flex-end" }}
            sx={{ width: { xs: "100%", lg: "auto" }, maxWidth: { lg: 760 } }}
          >
            <TextField
              size="small"
              label="Status"
              select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              sx={{ minWidth: { xs: "100%", sm: 160 }, flex: { xs: "1 1 100%", sm: "0 0 160px", md: "0 0 180px" } }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="granted">Granted</MenuItem>
              <MenuItem value="revision_requested">Revision Requested</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Search name, quote, date"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ minWidth: { xs: "100%", sm: 220 }, flex: { xs: "1 1 100%", sm: "1 1 220px", md: "1 1 260px" } }}
            />
            <Button
              variant="outlined"
              onClick={() => void loadRows()}
              sx={{ minHeight: 40, px: 2.25, whiteSpace: "nowrap", flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
            >
              Filter
            </Button>
            <Button
              component={RouterLink}
              to="/admin/quotations/new"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              sx={{ minHeight: 40, px: 2.5, whiteSpace: "nowrap", flex: { xs: "1 1 100%", sm: "0 0 auto" } }}
            >
              New quotation
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid rgba(118, 93, 68, 0.12)",
          background: "rgba(255, 250, 244, 0.96)",
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto"
        }}
      >
        <Table sx={{ minWidth: { xs: 1080, md: 1180, lg: 1320 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Quote</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Event</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Event Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Categories</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Issue Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                    <Typography sx={{ fontWeight: 800 }}>{row.quote_number}</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {row.title}
                  </Typography>
                </TableCell>
                <TableCell>{row.customer_name || "-"}</TableCell>
                <TableCell>{row.event_title || "-"}</TableCell>
                <TableCell>{formatDisplayDate(row.event_start_at)}</TableCell>
                <TableCell sx={{ width: { xs: 180, md: 220 }, maxWidth: { xs: 180, md: 220 } }}>
                  {(() => {
                    const categoryText = getQuotationCategoryNames(row);

                    return (
                  <Typography
                    component="button"
                    variant="body2"
                    type="button"
                    title={categoryText || undefined}
                    onClick={(event) => openCategoryPopover(event, categoryText)}
                    sx={{
                      color: "text.primary",
                      fontWeight: 600,
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.35,
                      p: 0,
                      border: 0,
                      background: "transparent",
                      font: "inherit",
                      textAlign: "left",
                      cursor: categoryText ? "pointer" : "default"
                    }}
                  >
                    {categoryText || "-"}
                  </Typography>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <Chip label={row.status} color={statusColor(row.status)} size="small" />
                </TableCell>
                <TableCell>{currency.format(Number(row.grand_total || 0))}</TableCell>
                <TableCell>{row.issue_date ? new Date(row.issue_date).toLocaleDateString("en-IN") : "-"}</TableCell>
                <TableCell sx={{ minWidth: { xs: 260, lg: 360 } }}>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    <Button component={RouterLink} to={`/admin/quotations/${row.id}/edit`} size="small" startIcon={<EditRoundedIcon />}>
                      Edit
                    </Button>
                    <Button component={RouterLink} to={`/admin/quotations/${row.id}/print`} size="small" startIcon={<PrintRoundedIcon />}>
                      Print
                    </Button>
                    <Button size="small" startIcon={<ContentCopyRoundedIcon />} onClick={() => void duplicate(row.id)}>
                      Duplicate
                    </Button>
                    {row.status !== "granted" ? (
                      <Button size="small" color="success" startIcon={<TaskAltRoundedIcon />} onClick={() => void updateStatus(row.id, "granted")}>
                        Grant
                      </Button>
                    ) : null}
                    {row.status !== "rejected" ? (
                      <Button size="small" color="error" startIcon={<DoDisturbAltRoundedIcon />} onClick={() => void updateStatus(row.id, "rejected")}>
                        Reject
                      </Button>
                    ) : null}
                    <Button size="small" color="error" startIcon={<DeleteOutlineRoundedIcon />} onClick={() => setDeleteTarget(row)}>
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && !loading ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                    No data found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Popover
        open={Boolean(categoryPopover)}
        anchorEl={categoryPopover?.anchorEl || null}
        onClose={closeCategoryPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            maxWidth: 320,
            p: 1.5,
            borderRadius: 2,
            border: "1px solid rgba(118, 93, 68, 0.14)",
            boxShadow: "0 12px 32px rgba(36, 24, 12, 0.16)"
          }
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
          {categoryPopover?.text}
        </Typography>
      </Popover>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Delete quotation?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary" }}>
            Are you sure you want to delete quotation <strong>{deleteTarget?.quote_number}</strong>? This will remove it from the active quotation list.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void deleteQuotation()} disabled={loading}>
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
